import { z } from 'zod/v4';
import {
  calculationTypeSchema,
  createThresholdOptionsSchema,
  sortOptionSchema,
  stepOptionsSchema,
  unitOptionsSchema,
} from '../../utils';
import { createDefinitionSchema } from '../../utils/helpers';

const gaugeChartStepOptionsSchema = stepOptionsSchema.extend({
  show_value: z.boolean().optional(),
});

export const gaugeChartThresholdOptionsSchema = createThresholdOptionsSchema(gaugeChartStepOptionsSchema).extend({
  base: gaugeChartStepOptionsSchema.omit({ value: true }).optional(),
});

export interface GaugeChartStepOption extends z.TypeOf<typeof gaugeChartStepOptionsSchema> {}
export interface GaugeChartThresholdOptions extends z.TypeOf<typeof gaugeChartThresholdOptionsSchema> {}

const gaugeChartModeSchema = z.enum(['Gauge', 'Bar']).optional().describe('Show as gauges or bars');
export type GaugeChartMode = z.infer<typeof gaugeChartModeSchema>;

/**
 * Type for the spec-driven `DashboardPanel` variant of the gauge chart.
 */
export interface GaugeChartSpec extends z.TypeOf<typeof gaugeChartSpecSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the gauge chart.
 */
export const gaugeChartSpecSchema = z.object({
  calculation: calculationTypeSchema.describe('DEPRECATED').optional(),
  unit: unitOptionsSchema.optional(),
  thresholds: gaugeChartThresholdOptionsSchema.optional(),
  max: z.number().optional(),
  mode: gaugeChartModeSchema,
  sort: sortOptionSchema.optional(),
});

/**
 * The schema for a GaugeChart `DashboardPanel` definition.
 */
export interface GaugeChartDefinition extends z.TypeOf<typeof gaugeChartDefinitionSchema> {}
export const gaugeChartDefinitionSchema = createDefinitionSchema('GaugeChart', gaugeChartSpecSchema);

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the gauge chart.
 *
 * Currently, it is the same as the spec-driven variant, but it may need to change in the future to
 * do things like flatten a specific property or some other transform (examples can be found in pie chart).
 */
export const gaugeChartVizSchema = gaugeChartSpecSchema;

/**
 * Base type for the props-driven `Viz` variant of the gauge chart generated from the Zod schema. This interface should be extended in `viz-framework`
 * to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 *
 * This type is currently not camelCased because the gauge chart has older code that is largely still
 * dependent on the snake_case properties, and it will require some significant refactoring to move it to
 * camelCase.
 */
export interface GaugeChartPropsBase extends z.TypeOf<typeof gaugeChartVizSchema> {}
